import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    AlertCircle,
    BadgePercent,
    Check,
    CheckCircle2,
    ChevronDown,
    Clock,
    Copy,
    Crown,
    Download,
    Globe,
    Home,
    PartyPopper,
    Receipt,
    RefreshCw,
    ShieldCheck,
    Sparkles,
    Store as StoreIcon,
    Ticket
} from "lucide-react";

import { fetchPaymentConfirmation } from "../../../store/paymentSlices/paymentConfirmationSlice";
import { getVoucherOrder } from "../../../services/api";
import useGoerOneAuth from "../../../hooks/useGoerOneAuth";
/* ───────── helpers ───────── */

const formatINR = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n) || 0);

const formatCoins = (n) => {
    const v = Number(n) || 0;
    const text = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(v);
    return `${text} ${v === 1 ? "GoerCoin" : "GoerCoins"}`;
};

const formatDate = (value, withTime = false) => {
    if (!value) return null;
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    });
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const MEMBERSHIP_GST_RATE = 0.18;

// TODO: invoice download url will be provided by the backend later.
// Until then this returns null and the button stays disabled.
const getInvoiceUrl = (data) => data?.invoiceUrl || null;

const maskCard = (num = "") => {
    const last4 = String(num).slice(-4);
    return last4 ? `\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ${last4}` : "";
};

/* Copy per purchase type. When voucher / membership-only purchases get their own
   API data, add/adjust the wording here. */
const PURCHASE_COPY = {
    reward: {
        title: "Congratulations!",
        subtitle: "Your payment was successful",
        message: (store) =>
            store ? `Thank you for your purchase at ${store}. Your order is confirmed.` : "Thank you for your purchase. Your order is confirmed.",
    },
    voucher: {
        title: "Congratulations!",
        subtitle: "Your voucher is ready",
        message: (store) => (store ? `Your voucher from ${store} is confirmed.` : "Your voucher is confirmed."),
    },
    membership: {
        title: "Welcome aboard!",
        subtitle: "Your membership is active",
        message: () => "Your membership is confirmed. Enjoy the benefits.",
    },
    unknown: {
        title: "Congratulations!",
        subtitle: "Your payment was successful",
        message: () => "Thank you. Your order is confirmed.",
    },
};

/* ───────── small UI pieces ───────── */

const CONFETTI_COLORS = ["#0B67FC", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6", "#06B6D4"];
function Confetti() {
    const pieces = useMemo(
        () =>
            Array.from({ length: 36 }, (_, i) => ({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * 1.2,
                duration: 2.8 + Math.random() * 2.2,
                size: 6 + Math.random() * 8,
                color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                round: i % 3 === 0,
            })),
        []
    );
    return (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full overflow-hidden" aria-hidden="true">
            {pieces.map((p) => (
                <span
                    key={p.id}
                    className="confetti-piece absolute top-0 block"
                    style={{
                        left: `${p.left}%`,
                        width: p.size,
                        height: p.round ? p.size : p.size * 0.5,
                        backgroundColor: p.color,
                        borderRadius: p.round ? "9999px" : "2px",
                        animationDelay: `${p.delay}s`,
                        animationDuration: `${p.duration}s`,
                    }}
                />
            ))}
        </div>
    );
}

function DetailRow({ label, value, valueClass = "text-slate-900" }) {
    if (value === undefined || value === null || value === "") return null;
    return (
        <li className="flex items-start justify-between gap-4 py-3">
            <span className="text-sm text-slate-500">{label}</span>
            <span className={`text-right text-sm font-semibold ${valueClass}`}>{value}</span>
        </li>
    );
}

function MembershipCardVisual({ membership }) {
    const m = membership;
    return (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#062E6C] via-[#0B4FC4] to-[#0B67FC] p-5 text-white shadow-xl shadow-[#0B67FC]/25">
            <span className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />
            <span className="absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-white/10" />

            <div className="relative flex items-start justify-between">
                <div className="flex items-center gap-2">
                    {m.logo ? (
                        <img src={m.logo} alt="" className="h-8 w-8 rounded-md bg-white/90 object-contain p-1" />
                    ) : (
                        <Crown className="h-6 w-6 text-amber-300" />
                    )}
                    <div>
                        <p className="text-sm font-bold leading-tight">{m.programName}</p>
                        {m.tagline && <p className="text-[11px] text-white/70">{m.tagline}</p>}
                    </div>
                </div>
                {m.cardType && (
                    <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider">
                        {m.cardType}
                    </span>
                )}
            </div>

            <p className="relative mt-8 font-mono text-lg tracking-widest sm:text-xl">{maskCard(m.cardNumber)}</p>

            <div className="relative mt-5 flex items-end justify-between text-xs">
                <div>
                    <p className="text-white/60">Member since</p>
                    <p className="font-semibold">{formatDate(m.activationDate) || "-"}</p>
                </div>
                <div className="text-right">
                    <p className="text-white/60">Valid till</p>
                    <p className="font-semibold">{formatDate(m.expiryDate) || "-"}</p>
                </div>
            </div>
        </div>
    );
}

function Skeleton() {
    return (
        <div className="mt-8 animate-pulse space-y-4">
            <div className="h-24 rounded-2xl bg-slate-200/70" />
            <div className="h-56 rounded-2xl bg-slate-200/70" />
            <div className="h-44 rounded-2xl bg-slate-200/70" />
        </div>
    );
}

/* ───────── page ───────── */

const StoreConfirmation = () => {
    const dispatch = useDispatch();
    const { state: routeState } = useLocation();
    const [searchParams] = useSearchParams();
    const referenceNumber = searchParams.get("ref") || routeState?.referenceNumber;

    const { details, referenceNo: loadedRef, status: fetchStatus, error } = useSelector((s) => s.paymentConfirmation);
    const data = loadedRef === referenceNumber ? details : null;
    const loading = !data && fetchStatus !== "failed";
    const failed = !data && fetchStatus === "failed";

    const [copied, setCopied] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const pollCount = useRef(0);

    const { refreshUser } = useGoerOneAuth();
    const userRefreshed = useRef(false);

    const load = () => dispatch(fetchPaymentConfirmation(referenceNumber));

    useEffect(() => {
        window.scrollTo(0, 0);
        if (referenceNumber) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referenceNumber]);

    // while pending, quietly re-check a few times (every 8s, max 6 times)
    useEffect(() => {
        if (!referenceNumber || data?.status !== "pending" || pollCount.current >= 3) return undefined;
        const t = setTimeout(() => {
            pollCount.current += 1;
            load();
        }, 8000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, referenceNumber]);

    useEffect(() => {
    if (data?.status === "completed" && Number(data?.mep) === 1 && !userRefreshed.current) {
        userRefreshed.current = true;
        refreshUser();
    }
    }, [data?.status, data?.mep, refreshUser]);

    // opened directly without a reference number -> nothing to confirm
    if (!referenceNumber) return <Navigate to="/web/home" replace />;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(referenceNumber);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard not available: ignore */
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        load().finally(() => setRefreshing(false));
    };

    const isPending = data?.status === "pending";
    const isDone = data?.status === "completed";
    const copy = PURCHASE_COPY[data?.purchaseType] || PURCHASE_COPY.unknown;
    const storeName = routeState?.storeName;
    const storeId = data?.storeId ?? routeState?.storeId;
    const storeLink = storeId ? `/web/store-details?storeId=${storeId}` : null;

    // membership bought on this order -> show its fee + 18% GST
   const membershipPurchase = Number(data?.mep) === 1; // true, 1 and "1" pass; false, 0, "0" and missing don'
    // const membershipPurchase = !!(data?.membership_purchase ?? data?.membershipPurchase);
    // TODO: confirm which field carries the fee in the confirmation API
   const membershipFee =membershipPurchase ? round2(data?.membership?.membershipAmount) :0;
    const membershipGst = round2(membershipFee * MEMBERSHIP_GST_RATE);
    // raw API pieces; the slice must pass these through: transactions, data (redemption), rdm, payment
    const txn = data?.transaction ?? data?.transactions ?? null;
    const redemption = Number(data?.rdm) === 1 ? data?.redemption ?? data?.redeem ?? null : null;
    const redeemed = !!redemption;
    // paid bill without reward: data is null, so the bill amount only exists on the transaction
    // (membership-only also has a transaction, but that amount is fee + GST, not a bill)
    const paidOnly = !redeemed && !membershipPurchase && Number(txn?.amount) > 0;
    const billAmount =
        Number(data?.orderAmount) > 0
            ? Number(data.orderAmount)
            : redeemed
            ? Number(redemption.order_amount) || 0
            : paidOnly
            ? Number(txn.amount) || 0
            : 0;
    const customerDiscount =
        Number(data?.discountCustomer) > 0 ? Number(data.discountCustomer) : redeemed ? Number(redemption.discountCust) || 0 : 0;
     // If a voucher was used during redemption, subtract its amount from the bill too.
    const usedVoucher =
        data?.usedVoucher === true ||
        data?.usedVoucher === 1 ||
        data?.usedVoucher === "1" ||
        data?.used_voucher === true ||
        data?.used_voucher === 1 ||
        data?.used_voucher === "1";
    const voucherAmount = usedVoucher ? Number(data?.voucherAmount ?? data?.voucher_amount) || 0 : 0;
    const payable = Math.max(billAmount - customerDiscount - voucherAmount, 0);
        // const payable = Math.max(billAmount - customerDiscount, 0);
    const pointsUsed = redeemed ? Number(redemption.points_deducted) || 0 : 0;
    const showTotalPaid = membershipPurchase || billAmount > 0;
    const orderDate = data?.date || redemption?.redemption_date || txn?.created_at;
    const totalPaid = round2((billAmount > 0 ? payable : 0) + membershipFee + membershipGst);
    const invoiceUrl = getInvoiceUrl(data);
    return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#EAF2FF] via-white to-slate-50 px-4 py-10 sm:py-16">
            <style>{`
                @keyframes confetti-fall {
                    0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
                    100% { transform: translateY(105vh) rotate(720deg); opacity: 0; }
                }
                .confetti-piece { animation-name: confetti-fall; animation-timing-function: ease-in; animation-fill-mode: both; }
                @keyframes pop-in {
                    0%   { transform: scale(0.4); opacity: 0; }
                    60%  { transform: scale(1.12); opacity: 1; }
                    100% { transform: scale(1); }
                }
                .pop-in { animation: pop-in 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
                @keyframes ring-pulse {
                    0%   { transform: scale(1); opacity: 0.55; }
                    100% { transform: scale(1.9); opacity: 0; }
                }
                .ring-pulse { animation: ring-pulse 1.8s ease-out 0.4s 2 both; }
                @keyframes fade-up {
                    0%   { transform: translateY(16px); opacity: 0; }
                    100% { transform: translateY(0); opacity: 1; }
                }
                .fade-up { animation: fade-up 0.6s ease-out both; }
                @media (prefers-reduced-motion: reduce) {
                    .confetti-piece, .pop-in, .ring-pulse, .fade-up { animation: none !important; }
                    .confetti-piece { display: none; }
                }
            `}</style>

            {isDone && <Confetti />}

            <div className="relative mx-auto w-full max-w-lg">
                {/* Hero */}
                <div className="flex flex-col items-center text-center">
                    {loading ? (
                        <div className="mb-4 h-24 w-24 animate-pulse rounded-full bg-slate-200" />
                    ) : failed ? (
                        <div className="pop-in mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-slate-400 to-slate-600 shadow-lg">
                            <AlertCircle className="h-14 w-14 text-white" strokeWidth={2} />
                        </div>
                    ) : isPending ? (
                        <div className="relative mb-4 flex h-24 w-24 items-center justify-center">
                            <span className="ring-pulse absolute inset-0 rounded-full bg-amber-400/40" />
                            <span className="pop-in relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/30">
                                <Clock className="h-14 w-14 text-white" strokeWidth={2} />
                            </span>
                        </div>
                    ) : (
                        <div className="relative mb-4 flex h-24 w-24 items-center justify-center">
                            <span className="ring-pulse absolute inset-0 rounded-full bg-emerald-400/40" />
                            <span className="pop-in relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/30">
                                <CheckCircle2 className="h-14 w-14 text-white" strokeWidth={2} />
                            </span>
                        </div>
                    )}

                    {!loading && (
                        <>
                            <h1
                                className="fade-up flex items-center gap-2 text-3xl font-extrabold text-[#062E6C] sm:text-4xl"
                                style={{ animationDelay: "0.25s" }}
                            >
                                {failed ? "Order received" : isPending ? "Payment pending" : copy.title}
                                {isDone && <PartyPopper className="h-7 w-7 text-amber-500" />}
                            </h1>
                            <p
                                className={`fade-up mt-2 text-base font-medium ${
                                    isPending ? "text-amber-600" : failed ? "text-slate-500" : "text-emerald-600"
                                }`}
                                style={{ animationDelay: "0.35s" }}
                            >
                                {failed ? "We couldn't load the details right now" : isPending ? "We're confirming your payment" : copy.subtitle}
                            </p>
                            <p className="fade-up mt-2 max-w-sm text-sm text-slate-500" style={{ animationDelay: "0.45s" }}>
                                {failed
                                    ? error || "Please try again in a moment. Your reference number is below."
                                    : isPending
                                    ? "This can take a few minutes. You'll see the confirmation here once it's completed. No need to pay again."
                                    : copy.message(storeName)}
                            </p>
                        </>
                    )}
                </div>

                {loading && <Skeleton />}

                {!loading && (
                    <>
                        {/* You saved banner */}
                        {isDone && customerDiscount > 0 && (
                            <div
                                className="fade-up mt-6 flex items-center gap-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-4"
                                style={{ animationDelay: "0.5s" }}
                            >
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md shadow-emerald-500/30">
                                    <BadgePercent className="h-6 w-6" />
                                </span>
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider text-emerald-700">You saved</p>
                                    <p className="text-2xl font-extrabold text-emerald-700">{formatINR(customerDiscount)}</p>
                                </div>
                                <Sparkles className="ml-auto h-6 w-6 text-amber-400" />
                            </div>
                        )}

                        {/* Cashback banner: pending / approved */}
                        {data.cashbackAmount > 0 && (
                            <div
                                className={`fade-up mt-4 flex items-start gap-4 rounded-2xl border px-5 py-4 ${
                                    data.cashbackApproved
                                        ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50"
                                        : "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50"
                                }`}
                                style={{ animationDelay: "0.55s" }}
                            >
                                <span className="relative flex h-12 w-12 shrink-0 items-center justify-center">
                                    {!data.cashbackApproved && (
                                        <span className="absolute inset-0 animate-ping rounded-full bg-amber-400/30" />
                                    )}
                                    <span
                                        className={`relative flex h-12 w-12 items-center justify-center rounded-full text-white shadow-md ${
                                            data.cashbackApproved
                                                ? "bg-emerald-500 shadow-emerald-500/30"
                                                : "bg-amber-500 shadow-amber-500/30"
                                        }`}
                                    >
                                        {data.cashbackApproved ? <Check className="h-6 w-6" strokeWidth={3} /> : <Clock className="h-6 w-6" />}
                                    </span>
                                </span>
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <p
                                            className={`text-xs font-medium uppercase tracking-wider ${
                                                data.cashbackApproved ? "text-emerald-700" : "text-amber-700"
                                            }`}
                                        >
                                            {data.cashbackApproved ? "GoerCoins approved" : "GoerCoins pending"}
                                        </p>
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                                data.cashbackApproved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                                            }`}
                                        >
                                            {data.cashbackApproved ? "Credited" : "Awaiting approval"}
                                        </span>
                                    </div>
                                    <p className={`text-2xl font-extrabold ${data.cashbackApproved ? "text-emerald-700" : "text-amber-700"}`}>
                                        {formatCoins(data.cashbackAmount)}
                                    </p>
                                    <p className={`mt-0.5 text-xs font-medium ${data.cashbackApproved ? "text-emerald-800/80" : "text-amber-800/80"}`}>
                                        {data.cashbackApproved
                                            ? "Your GoerCoins has been approved and credited."
                                            : "Will be credited automatically after approval."}
                                    </p>
                                </div>
                                <Sparkles className={`ml-auto h-6 w-6 shrink-0 ${data.cashbackApproved ? "text-emerald-400" : "text-amber-400"}`} />
                            </div>
                        )}

                        {/* Ticket card */}
                        <div
                            className="fade-up mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60"
                            style={{ animationDelay: "0.6s" }}
                        >
                            <div className="bg-gradient-to-r from-[#0B67FC] to-[#062E6C] px-6 py-5 text-white">
                                <p className="text-xs font-medium uppercase tracking-widest text-white/70">Reference number</p>
                                <div className="mt-1 flex items-center justify-between gap-3">
                                    <span className="font-mono text-xl font-bold tracking-widest sm:text-2xl">{referenceNumber}</span>
                                    <button
                                        type="button"
                                        onClick={handleCopy}
                                        className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/25"
                                    >
                                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                        {copied ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </div>

                            {data && (
                                <>
                                    <div className="relative">
                                        <span className="absolute -left-3 -top-3 h-6 w-6 rounded-full bg-[#F1F6FF]" />
                                        <span className="absolute -right-3 -top-3 h-6 w-6 rounded-full bg-[#F1F6FF]" />
                                        <div className="mx-4 border-t-2 border-dashed border-slate-200" />
                                    </div>

                                    <div className="px-6 pb-5 pt-3">
                                        <h2 className="flex items-center gap-2 pb-1 text-sm font-semibold text-slate-900">
                                            <Receipt className="h-4 w-4 text-[#0B67FC]" /> Order details
                                        </h2>
                                        <ul className="divide-y divide-slate-100">
                                            <DetailRow label="Store" value={storeName} />
                                            <DetailRow label="Bill amount" value={billAmount > 0 && customerDiscount > 0 ? formatINR(billAmount) : null} />
                                            <DetailRow
                                                label="Discount"
                                                value={customerDiscount > 0 ? `- ${formatINR(customerDiscount)}` : null}
                                                valueClass="text-emerald-600"
                                            />
                                             <DetailRow
                                                label="Voucher used"
                                                value={voucherAmount > 0 ? `- ${formatINR(voucherAmount)}` : null}
                                                valueClass="text-emerald-600"
                                            />
                                            <DetailRow
                                                label="Bill after discounts"
                                                value={billAmount > 0 && (customerDiscount > 0 || voucherAmount > 0) ? formatINR(payable) : null}
                                            />
                                            {/* <DetailRow label="Bill after discount" value={billAmount > 0 && customerDiscount > 0 ? formatINR(payable) : null} /> */}
                                            {redeemed && (
                                                <>
                                                    <DetailRow label="Points used" value={pointsUsed > 0 ? formatCoins(pointsUsed) : null} />
                                                </>
                                            )}
                                            {membershipPurchase &&
                                                (membershipFee > 0 ? (
                                                    <>
                                                        <DetailRow label="Membership fee" value={formatINR(membershipFee)} />
                                                        <DetailRow label="GST on membership (18%)" value={formatINR(membershipGst)} />
                                                    </>
                                                ) : (
                                                    <DetailRow label="Membership" value="Free" valueClass="text-emerald-600" />
                                                ))}
                                            {showTotalPaid && <DetailRow label="Total paid" value={formatINR(totalPaid)} />}
                                            <DetailRow label="Date" value={formatDate(orderDate, true)} />
                                            <DetailRow
                                                label="GoerCoins earned"
                                                value={data.cashbackAmount > 0
                                                        ? `${formatCoins(data.cashbackAmount)} · ${data.cashbackApproved ? "Approved" : "Pending"}`
                                                        : null
                                                }
                                                valueClass={data.cashbackApproved ? "text-emerald-600" : "text-amber-600"}
                                            />
                                            <DetailRow label="Transaction ID" value={data.transactionId} />
                                            <DetailRow
                                                label="Status"
                                                value={isDone ? "Completed" : "Pending"}
                                                valueClass={isDone ? "text-emerald-600" : "text-amber-600"}
                                            />
                                        </ul>
                                        {membershipPurchase && membershipFee > 0 && isDone && (
                                            invoiceUrl ? (
                                                <a
                                                    href={invoiceUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    download
                                                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-[#0B67FC] bg-white px-5 py-2.5 text-sm font-semibold text-[#0B67FC] transition hover:bg-[#0B67FC]/5"
                                                >
                                                    <Download className="h-4 w-4" /> Download invoice
                                                </a>
                                            ) : (
                                                <button
                                                    type="button"
                                                    disabled
                                                    title="Invoice will be available soon"
                                                    className="mt-4 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-5 py-2.5 text-sm font-semibold text-slate-400"
                                                >
                                                    <Download className="h-4 w-4" /> Download invoice
                                                </button>
                                            )
                                        )}
                                        <p className="mt-4 flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                                            <ShieldCheck className="h-4 w-4" /> Secured payment
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Membership card */}
                        {data?.membership && (
                            <div className="fade-up mt-6" style={{ animationDelay: "0.75s" }}>
                                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Crown className="h-4 w-4 text-amber-500" /> Your membership card
                                </h2>
                                <MembershipCardVisual membership={data.membership} />

                                {(data.membership.welcomeCoin > 0 || data.membership.services.length > 0) && (
                                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
                                        {data.membership.welcomeCoin > 0 && (
                                            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-600">
                                                <Sparkles className="h-4 w-4" /> {data.membership.welcomeCoin} welcome coins on this card
                                            </p>
                                        )}
                                        {data.membership.services.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {data.membership.services.map((svc) => (
                                                    <span
                                                        key={svc}
                                                        className="rounded-full bg-[#EAF2FF] px-3 py-1 text-xs font-medium text-[#0B67FC]"
                                                    >
                                                        {svc}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="fade-up mt-6 flex flex-col gap-3 sm:flex-row" style={{ animationDelay: "0.85s" }}>
                            {(isPending || failed) && (
                                <button
                                    type="button"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                                >
                                    <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                                    {refreshing ? "Checking..." : isPending ? "Refresh status" : "Try again"}
                                </button>
                            )}
                            {isDone && storeLink && (
                                <Link
                                    to={storeLink}
                                    className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                                >
                                    <StoreIcon className="h-4 w-4" /> Back to store
                                </Link>
                            )}
                            <Link
                                to="/web/home"
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#0B67FC] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[#0B67FC]/30 transition hover:bg-[#0954d0]"
                            >
                                <Home className="h-4 w-4" /> Go to home
                            </Link>
                        </div>

                        <p className="mt-6 text-center text-xs text-slate-400">
                            Keep your reference number handy for any support queries.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

/* ───────── voucher order confirmation ───────── */

const VOUCHER_REF_RE = /^VO\d{6}[A-Z0-9]{4,}$/;

const VOUCHER_STYLES = `
    @keyframes confetti-fall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 1; } 100% { transform: translateY(105vh) rotate(720deg); opacity: 0; } }
    .confetti-piece { animation-name: confetti-fall; animation-timing-function: ease-in; animation-fill-mode: both; }
    @keyframes pop-in { 0% { transform: scale(0.4); opacity: 0; } 60% { transform: scale(1.12); opacity: 1; } 100% { transform: scale(1); } }
    .pop-in { animation: pop-in 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
    @keyframes ring-pulse { 0% { transform: scale(1); opacity: 0.55; } 100% { transform: scale(1.9); opacity: 0; } }
    .ring-pulse { animation: ring-pulse 1.8s ease-out 0.4s 2 both; }
    @keyframes fade-up { 0% { transform: translateY(16px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
    .fade-up { animation: fade-up 0.6s ease-out both; }
    @media (prefers-reduced-motion: reduce) { .confetti-piece, .pop-in, .ring-pulse, .fade-up { animation: none !important; } .confetti-piece { display: none; } }
`;

function CopyChip({ text, label = "Copy", light = false }) {
    const [copied, setCopied] = useState(false);
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard not available */
        }
    };
    return (
        <button
            type="button"
            onClick={onCopy}
            className={`flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                light ? "bg-white/15 text-white hover:bg-white/25" : "bg-[#0B67FC]/10 text-[#0B67FC] hover:bg-[#0B67FC]/20"
            }`}
        >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : label}
        </button>
    );
}

 // voucher.redemption_type: 1 = online, 2 = offline, 3 = both
    const REDEEM_INFO = {
        1: { label: "Online only", hint: "Redeem on the app or website", icons: ["online"] },
        2: { label: "In-store only", hint: "Show the code at the store", icons: ["offline"] },
        3: { label: "Online & in-store", hint: "Redeem online or at the store", icons: ["online", "offline"] },
    };

function VoucherTicket({ v }) {
    const [showTerms, setShowTerms] = useState(false);
    const isPercent = Number(v.discount_type) === 2;
    const customerShare = Number(v.voucher?.customer_share) || 0;
    const value = Number(v.discount_value) || 0;
    const cap = Number(v.max_discount_value) || 0;
    const used = v.status === "used" || !!v.used_at;
    const expired = v.status === "expired";
    const live = !used && !expired;

    // Actual discount amount
    const actualDiscount = isPercent ? cap : value;

    // Customer's share of the discount
    const customerDiscount = (actualDiscount * customerShare) / 100;

    const badge = used
        ? { text: "Used", cls: "bg-slate-100 text-slate-600" }
        : expired
        ? { text: "Expired", cls: "bg-rose-100 text-rose-700" }
        : { text: "Active", cls: "bg-emerald-100 text-emerald-700" };

    const validity = v.valid_to
        ? `Valid till ${formatDate(v.valid_to)}`
        : v.valid_from
        ? `Valid from ${formatDate(v.valid_from)}`
        : "No expiry date";

        const redeem = REDEEM_INFO[Number(v.voucher?.redemption_type)] || null;

    return (
        <div
            className={`relative overflow-hidden rounded-2xl border bg-white shadow-lg ${
                live ? "border-[#0B67FC]/20 shadow-[#0B67FC]/10" : "border-slate-200 opacity-80 shadow-slate-200/60"
            }`}
        >
            <div className="flex">
                {/* stub with the discount */}
                <div
                    className={`relative flex w-28 shrink-0 flex-col items-center justify-center px-2 py-5 text-center text-white sm:w-32 ${
                        live ? "bg-gradient-to-br from-[#0B67FC] to-[#062E6C]" : "bg-gradient-to-br from-slate-400 to-slate-500"
                    }`}
                >
                    <span className="absolute -right-8 -top-8 h-20 w-20 rounded-full bg-white/10" />
                    <Ticket className="relative mb-1 h-5 w-5 text-white/70" />
                    <p className="relative text-2xl font-extrabold leading-none">{isPercent ? `${value}%` : formatINR(customerDiscount)}</p>
                    <p className="relative mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">Off</p>
                    {isPercent && cap > 0 && <p className="relative mt-1 text-[10px] text-white/70">up to {formatINR(cap)}</p>}
                </div>

                {/* ticket body */}
                <div className="relative min-w-0 flex-1 border-l-2 border-dashed border-slate-200 px-4 py-4">
                    <span className="absolute -left-2.5 -top-2.5 h-5 w-5 rounded-full bg-[#F3F7FF]" />
                    <span className="absolute -bottom-2.5 -left-2.5 h-5 w-5 rounded-full bg-[#F3F7FF]" />

                    <div className="flex items-start justify-between gap-2">
                        <p className="truncate text-sm font-bold text-slate-900">{v.voucher_name}</p>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badge.cls}`}>
                            {badge.text}
                        </span>
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-dashed border-[#0B67FC]/30 bg-[#0B67FC]/5 px-3 py-2">
                        <div className="min-w-0">
                            <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Voucher code</p>
                            <p className={`truncate font-mono text-base font-bold tracking-widest ${live ? "text-[#062E6C]" : "text-slate-500 line-through"}`}>
                                {v.voucher_code}
                            </p>
                        </div>
                        {live && <CopyChip text={v.voucher_code} />}
                    </div>

                    {redeem && (
                        <div className="mt-2 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                            <span className="flex items-center -space-x-1">
                                {redeem.icons.includes("online") && (
                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-600 ring-2 ring-white">
                                        <Globe className="h-3.5 w-3.5" />
                                    </span>
                                )}
                                {redeem.icons.includes("offline") && (
                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-600 ring-2 ring-white">
                                        <StoreIcon className="h-3.5 w-3.5" />
                                    </span>
                                )}
                            </span>
                            <div className="min-w-0">
                                <p className="text-xs font-semibold leading-tight text-slate-800">{redeem.label}</p>
                                <p className="text-[11px] leading-tight text-slate-500">{redeem.hint}</p>
                            </div>
                        </div>
                    )}

                    <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                        <Clock className="h-3.5 w-3.5" />
                        {used && v.used_at ? `Used on ${formatDate(v.used_at, true)}` : validity}
                    </p>

                    {v.terms_condition && (
                        <>
                            <button
                                type="button"
                                onClick={() => setShowTerms((s) => !s)}
                                className="mt-2 flex cursor-pointer items-center gap-1 text-xs font-semibold text-[#0B67FC]"
                            >
                                Terms &amp; conditions
                                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showTerms ? "rotate-180" : ""}`} />
                            </button>
                            {showTerms && <p className="mt-1 whitespace-pre-line text-xs text-slate-500">{v.terms_condition}</p>}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function VoucherConfirmation({ referenceNumber, routeState }) {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [error, setError] = useState("");
    const [refreshing, setRefreshing] = useState(false);
    const pollCount = useRef(0);

    const load = async () => {
        try {
            const res = await getVoucherOrder(referenceNumber);
            if (res?.status === true && res?.data) {
                setData(res.data);
                setFailed(false);
            } else {
                throw res;
            }
        } catch (e) {
            setError(e?.message || "");
            setFailed(true);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referenceNumber]);

    // while pending, quietly re-check a few times (every 8s, max 6 times)
    useEffect(() => {
        if (data?.status !== "pending" || pollCount.current >= 6) return undefined;
        const t = setTimeout(() => {
            pollCount.current += 1;
            load();
        }, 8000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const handleRefresh = () => {
        setRefreshing(true);
        load().finally(() => setRefreshing(false));
    };

    const loading = !data && !failed;
    const isPaid = data?.status === "paid";
    const isPending = data?.status === "pending";
    const items = data?.items || [];
    const vouchers = data?.vouchers || [];
    const face = items.reduce((s, i) => s + (Number(i.line_total) || 0), 0);
    const discount = Number(data?.discount) || 0;
    const tax = Number(data?.tax_amount) || 0;
    const count = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
    const storeId = routeState?.storeId;
    const storeName = routeState?.storeName;

    const types = vouchers.map((v) => Number(v.voucher?.redemption_type));
    const onlineOnly = types.length > 0 && types.every((t) => t === 1);

    return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#EAF2FF] via-white to-slate-50 px-4 py-10 sm:py-16">
            <style>{VOUCHER_STYLES}</style>
            {isPaid && <Confetti />}

            <div className="relative mx-auto w-full max-w-lg">
                {/* Hero */}
                <div className="flex flex-col items-center text-center">
                    {loading ? (
                        <div className="mb-4 h-24 w-24 animate-pulse rounded-full bg-slate-200" />
                    ) : (
                        <div className="relative mb-4 flex h-24 w-24 items-center justify-center">
                            {isPaid && <span className="ring-pulse absolute inset-0 rounded-full bg-emerald-400/40" />}
                            <span
                                className={`pop-in relative flex h-24 w-24 items-center justify-center rounded-full shadow-lg ${
                                    isPaid
                                        ? "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/30"
                                        : isPending
                                        ? "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/30"
                                        : "bg-gradient-to-br from-slate-400 to-slate-600"
                                }`}
                            >
                                {isPaid ? (
                                    <CheckCircle2 className="h-14 w-14 text-white" strokeWidth={2} />
                                ) : isPending ? (
                                    <Clock className="h-14 w-14 text-white" strokeWidth={2} />
                                ) : (
                                    <AlertCircle className="h-14 w-14 text-white" strokeWidth={2} />
                                )}
                            </span>
                        </div>
                    )}

                    {!loading && (
                        <>
                            <h1 className="fade-up flex items-center gap-2 text-3xl font-extrabold text-[#062E6C] sm:text-4xl" style={{ animationDelay: "0.25s" }}>
                                {failed ? "Order received" : isPaid ? "Congratulations!" : isPending ? "Payment pending" : "Order update"}
                                {isPaid && <PartyPopper className="h-7 w-7 text-amber-500" />}
                            </h1>
                            <p
                                className={`fade-up mt-2 text-base font-medium ${isPaid ? "text-emerald-600" : isPending ? "text-amber-600" : "text-slate-500"}`}
                                style={{ animationDelay: "0.35s" }}
                            >
                                {failed
                                    ? "We couldn't load the details right now"
                                    : isPaid
                                    ? `Your ${count === 1 ? "voucher is" : "vouchers are"} ready`
                                    : isPending
                                    ? "We're confirming your payment"
                                    : `Order status: ${data?.status}`}
                            </p>
                            <p className="fade-up mt-2 max-w-sm text-sm text-slate-500" style={{ animationDelay: "0.45s" }}>
                                {failed
                                    ? error || "Please try again in a moment. Your reference number is below."
                                    : isPaid
                                    ? `Thank you${storeName ? ` for your purchase at ${storeName}` : ""}. ${
                                            onlineOnly ? "Use the code when you pay online to redeem it." : "Use the code to redeem it as shown on each voucher."
                                        }`
                                    : isPending
                                    ? "This can take a few minutes. Your vouchers will appear here once the payment is confirmed. No need to pay again."
                                    : "Please contact support if you need help with this order."}
                            </p>
                        </>
                    )}
                </div>

                {loading && (
                    <div className="mt-8 animate-pulse space-y-4">
                        <div className="h-24 rounded-2xl bg-slate-200/70" />
                        <div className="h-44 rounded-2xl bg-slate-200/70" />
                        <div className="h-32 rounded-2xl bg-slate-200/70" />
                    </div>
                )}

                {!loading && (
                    <>
                        {/* Saved banner */}
                        {isPaid && discount > 0 && (
                            <div className="fade-up mt-6 flex items-center gap-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-4" style={{ animationDelay: "0.5s" }}>
                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md shadow-emerald-500/30">
                                    <BadgePercent className="h-6 w-6" />
                                </span>
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider text-emerald-700">You saved</p>
                                    <p className="text-2xl font-extrabold text-emerald-700">{formatINR(discount)}</p>
                                </div>
                                <Sparkles className="ml-auto h-6 w-6 text-amber-400" />
                            </div>
                        )}

                        {/* Your vouchers */}
                        {vouchers.length > 0 && (
                            <div className="fade-up mt-6" style={{ animationDelay: "0.6s" }}>
                                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Ticket className="h-4 w-4 text-[#0B67FC]" /> Your {vouchers.length === 1 ? "voucher" : `vouchers (${vouchers.length})`}
                                </h2>
                                <div className="space-y-4">
                                    {vouchers.map((v) => (
                                        <VoucherTicket key={v.id ?? v.voucher_code} v={v} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Order card */}
                        <div className="fade-up mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60" style={{ animationDelay: "0.7s" }}>
                            <div className="bg-gradient-to-r from-[#0B67FC] to-[#062E6C] px-6 py-5 text-white">
                                <p className="text-xs font-medium uppercase tracking-widest text-white/70">Order number</p>
                                <div className="mt-1 flex items-center justify-between gap-3">
                                    <span className="font-mono text-xl font-bold tracking-widest sm:text-2xl">{data?.order_no || referenceNumber}</span>
                                    <CopyChip text={data?.order_no || referenceNumber} light />
                                </div>
                            </div>

                            {data && (
                                <div className="px-6 pb-5 pt-3">
                                    <h2 className="flex items-center gap-2 pb-1 text-sm font-semibold text-slate-900">
                                        <Receipt className="h-4 w-4 text-[#0B67FC]" /> Order details
                                    </h2>
                                    <ul className="divide-y divide-slate-100">
                                        <DetailRow label="Store" value={storeName} />
                                        {items.map((i) => (
                                            <DetailRow key={i.voucher_id} label={`${i.voucher_name} × ${i.quantity}`} value={formatINR(i.line_total)} />
                                        ))}
                                        <DetailRow label="Voucher discount" value={discount > 0 ? `- ${formatINR(discount)}` : null} valueClass="text-emerald-600" />
                                        <DetailRow label="Tax" value={tax > 0 ? formatINR(tax) : null} />
                                        <DetailRow label={isPaid ? "Total paid" : "Total"} value={formatINR(data.total_amount)} />
                                        <DetailRow label="Date" value={formatDate(data.paid_at || data.created_at, true)} />
                                        <DetailRow
                                            label="Status"
                                            value={isPaid ? "Paid" : isPending ? "Pending" : data.status}
                                            valueClass={isPaid ? "text-emerald-600" : isPending ? "text-amber-600" : "text-slate-700"}
                                        />
                                    </ul>
                                    <p className="mt-4 flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                                        <ShieldCheck className="h-4 w-4" /> Secured payment
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="fade-up mt-6 flex flex-col gap-3 sm:flex-row" style={{ animationDelay: "0.8s" }}>
                            {(isPending || failed) && (
                                <button
                                    type="button"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                                >
                                    <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                                    {refreshing ? "Checking..." : isPending ? "Refresh status" : "Try again"}
                                </button>
                            )}
                            {isPaid && storeId && (
                                <Link
                                    to={`/web/store-details?storeId=${storeId}`}
                                    className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                                >
                                    <StoreIcon className="h-4 w-4" /> Back to store
                                </Link>
                            )}
                            <Link
                                to="/web/home"
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#0B67FC] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[#0B67FC]/30 transition hover:bg-[#0954d0]"
                            >
                                <Home className="h-4 w-4" /> Go to home
                            </Link>
                        </div>

                        <p className="mt-6 text-center text-xs text-slate-400">Keep your order number handy for any support queries.</p>
                    </>
                )}
            </div>
        </div>
    );
}

/* ───────── entry: voucher orders get their own view ───────── */

const Confirmation = () => {
    const { state: routeState } = useLocation();
    const [searchParams] = useSearchParams();
    const referenceNumber = searchParams.get("ref") || routeState?.referenceNumber;

    const isVoucher = routeState?.purchaseType === "voucher" || VOUCHER_REF_RE.test(referenceNumber || "");

    if (!referenceNumber) return <Navigate to="/web/home" replace />;
    return isVoucher ? <VoucherConfirmation referenceNumber={referenceNumber} routeState={routeState} /> : <StoreConfirmation />;
};

export default Confirmation;